/**
 * CookieConsent — GDPR cookie consent banner.
 * Shows on first visit; stores decision in localStorage.
 * If declined, warns that some features (auth, CSRF) may not work.
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { LocalStorageKey } from '../stores/globalStore';

type ConsentValue = 'accepted' | 'declined' | null;

const CookieConsent: React.FC = () => {
  const [consent, setConsent] = useState<ConsentValue>(() => {
    try {
      return localStorage.getItem(LocalStorageKey.COOKIE_CONSENT) as ConsentValue;
    } catch {
      return null;
    }
  });

  const [showDeclinedWarning, setShowDeclinedWarning] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;
    const root = document.documentElement;
    const measure = () => root.style.setProperty('--consent-banner-height', `${banner.getBoundingClientRect().height}px`);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(banner);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      root.style.removeProperty('--consent-banner-height');
    };
  }, [consent, showDeclinedWarning]);

  useEffect(() => {
    if (consent === 'declined') {
      setShowDeclinedWarning(true);
      const timer = setTimeout(() => setShowDeclinedWarning(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [consent]);

  const handleAccept = () => {
    try { localStorage.setItem(LocalStorageKey.COOKIE_CONSENT, 'accepted'); } catch { /* noop */ }
    setConsent('accepted');
  };

  const handleDecline = () => {
    try { localStorage.setItem(LocalStorageKey.COOKIE_CONSENT, 'declined'); } catch { /* noop */ }
    setConsent('declined');
  };

  // Already decided — show nothing (or brief declined warning)
  if (consent === 'accepted') return null;

  if (consent === 'declined') {
    if (!showDeclinedWarning) return null;
    return (
      <div ref={bannerRef} style={styles.banner}>
        <span style={styles.warningText}>
          Cookies declined. Authentication and some features may not work correctly.
        </span>
      </div>
    );
  }

  // No decision yet — show consent banner
  return (
    <div ref={bannerRef} style={styles.banner}>
      <span style={styles.text}>
        This site uses cookies for authentication and security.
        By continuing, you accept our use of cookies.
      </span>
      <div style={styles.buttons}>
        <button onClick={handleAccept} style={styles.acceptBtn}>Accept</button>
        <button onClick={handleDecline} style={styles.declineBtn}>Decline</button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '14px 24px',
    background: 'var(--bg-surface, #1e293b)',
    borderTop: '1px solid var(--border-main, #334155)',
    zIndex: 10000,
    flexWrap: 'wrap' as const,
  },
  text: {
    color: 'var(--text-main, #f8fafc)',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  warningText: {
    color: 'var(--danger, #f87171)',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  buttons: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0,
  },
  acceptBtn: {
    padding: '8px 20px',
    borderRadius: '6px',
    border: 'none',
    background: 'var(--accent-primary, #4ade80)',
    color: '#0f172a',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
  },
  declineBtn: {
    padding: '8px 20px',
    borderRadius: '6px',
    border: '1px solid var(--border-main, #334155)',
    background: 'transparent',
    color: 'var(--text-muted, #94a3b8)',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
  },
};

export default CookieConsent;
